import React from 'react';
import { Star } from 'lucide-react';

/**
 * VendorRatingDistribution Component (Issue #956)
 *
 * Show rating breakdown with bar chart.
 *
 * Features:
 * - Horizontal bar chart for each star rating (1-5)
 * - Count and percentage for each rating
 * - Click bar to filter reviews
 * - Verified review count indicator
 */

interface VendorRatingDistributionProps {
  vendorId: number;
  ratingDistribution: Record<number, number>;
  totalReviews: number;
  verifiedCount: number;
  onFilterChange?: (rating: number | null) => void;
}

const VendorRatingDistribution: React.FC<VendorRatingDistributionProps> = ({
  ratingDistribution,
  totalReviews,
  verifiedCount,
  onFilterChange
}) => {
  const getPercentage = (count: number): number => {
    if (totalReviews === 0) return 0;
    return (count / totalReviews) * 100;
  };

  const getRatingColor = (rating: number): string => {
    if (rating === 5) return 'bg-green-500';
    if (rating === 4) return 'bg-teal-500';
    if (rating === 3) return 'bg-yellow-500';
    if (rating === 2) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getHoverColor = (rating: number): string => {
    if (rating === 5) return 'hover:bg-green-600';
    if (rating === 4) return 'hover:bg-primary';
    if (rating === 3) return 'hover:bg-yellow-600';
    if (rating === 2) return 'hover:bg-orange-600';
    return 'hover:bg-red-600';
  };

  if (totalReviews === 0) {
    return (
      <div className="text-center py-8 text-gray-500" data-testid="rating-distribution-empty">
        No reviews yet. Be the first to leave a review!
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="rating-distribution">
      {/* Verified reviews indicator */}
      <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
        <span>Rating Distribution</span>
        <span className="flex items-center gap-1" data-testid="verified-count">
          <span className="text-secondary font-semibold">{verifiedCount}</span>
          <span>verified {verifiedCount === 1 ? 'purchase' : 'purchases'}</span>
        </span>
      </div>

      {/* Rating bars (5 to 1) */}
      {[5, 4, 3, 2, 1].map((rating) => {
        const count = ratingDistribution[rating] || 0;
        const percentage = getPercentage(count);
        const isClickable = onFilterChange !== undefined;

        return (
          <div
            key={rating}
            className={`flex items-center gap-3 ${isClickable ? 'cursor-pointer group' : ''}`}
            onClick={() => isClickable && onFilterChange && onFilterChange(rating)}
            data-testid={`rating-bar-${rating}`}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onKeyPress={(e) => {
              if (isClickable && onFilterChange && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onFilterChange(rating);
              }
            }}
          >
            {/* Star rating label */}
            <div className="flex items-center gap-1 w-16">
              <span className="text-sm font-medium text-gray-700">{rating}</span>
              <Star className="w-4 h-4 text-yellow-500 fill-current" />
            </div>

            {/* Progress bar */}
            <div className="flex-1 bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className={`h-full ${getRatingColor(rating)} ${
                  isClickable ? getHoverColor(rating) : ''
                } transition-all duration-200`}
                style={{ width: `${percentage}%` }}
                data-testid={`rating-bar-fill-${rating}`}
              />
            </div>

            {/* Count and percentage */}
            <div className="flex items-center gap-2 w-24 text-sm text-gray-600">
              <span className="font-medium" data-testid={`rating-count-${rating}`}>
                {count}
              </span>
              <span className="text-gray-400">({percentage.toFixed(0)}%)</span>
            </div>
          </div>
        );
      })}

      {/* Clear filter button */}
      {onFilterChange && (
        <div className="mt-4">
          <button
            onClick={() => onFilterChange(null)}
            className="text-sm text-secondary hover:text-primary-dark underline"
            data-testid="clear-filter-button"
          >
            Show all reviews
          </button>
        </div>
      )}
    </div>
  );
};

export default VendorRatingDistribution;
